import {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {PerfectStoreSetUp} from '@/containers';
import PerfectStoreWizardLayout from '@/containers/PerfectStore/PerfectStoreWizardLayout';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  perfectStoreInitialFormData,
  resetPerfectStore,
  setPerfectStoreFormData
} from '@/store/slices/perfectStoreSlice';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {ROUTES} from '@/utils/constants/routesConstants';
import {usePerfectStore} from '@/hooks/usePerfectStore';
import {getModal} from '@/utils/CommonModal';
import {
  getPerfectStoreCancelModal,
  isPerfectStoreSetupInvalid
} from '@/pages/PerfectStoreUtils';
import {logger} from '@/helpers/logger';

const AddPerfectStore = () => {
  const location = useLocation();
  const isEdit = location?.state?.isEdit ?? false;
  const disableDraft = location?.state?.disableDraft ?? false;
  const fromWizard = location?.state?.fromWizard === true;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const perfectStore = useAppSelector((state) => state?.perfectStore);
  const shouldStartFresh = !isEdit && !fromWizard;
  const [formData, setFormData] = useState(() =>
    shouldStartFresh ? {...perfectStoreInitialFormData} : perfectStore?.formData
  );
  const [isCancel, setIsCancel] = useState(false);

  useEffect(() => {
    // Fresh create (Home / direct URL) must not keep persisted targetType.
    // Wizard step hops pass fromWizard so Setup can remount without wiping.
    if (!shouldStartFresh) return;
    dispatch(resetPerfectStore());
    setFormData({...perfectStoreInitialFormData});
  }, [dispatch, shouldStartFresh, isEdit, fromWizard]);

  const {
    handleDraft,
    handleNameValidation,
    isNameValid,
    setIsNameValid,
    handleValidationModalClose
  } = usePerfectStore();

  const getNavigationState = () => ({
    isEdit,
    disableDraft,
    fromWizard: true
  });

  const validateName = async () => {
    const name = formData?.name;

    if (!name || String(name).trim() === '') {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Target Name is mandatory',
          type: 'error'
        })
      );
      return false;
    }

    const params = {
      name,
      targetType: formData?.targetType ?? null
    };
    const resp = await handleNameValidation(params);
    const isValid = resp?.success === true;
    setIsNameValid(isValid);
    return isValid;
  };

  const handleNextAction = async () => {
    const isValidName = await validateName();
    if (!isValidName) return;

    dispatch(setPerfectStoreFormData(formData));
    navigate(ROUTES.PERFECT_STORE_DISPLAY, {
      state: getNavigationState()
    });
  };

  const handleDraftAction = async () => {
    try {
      const isValidName = await validateName();
      if (!isValidName) return;
      const response = await handleDraft(formData);
      if (response.success) {
        navigate(ROUTES.PERFECT_STORE);
      }
    } catch (error) {
      logger.error('Error saving Perfect Store draft:', error);
    }
  };

  const handleCancel = () => {
    setIsCancel(!isCancel);
  };

  const onClickPrimaryButton = () => {
    dispatch(resetPerfectStore());
    navigate(ROUTES.PERFECT_STORE);
  };

  const handleStepClick = (step) => {
    dispatch(setPerfectStoreFormData({...formData}));
    const stepRoutes = {
      2: ROUTES.PERFECT_STORE_DISPLAY,
      3: ROUTES.PERFECT_STORE_REVIEW
    };
    if (stepRoutes[step]) {
      navigate(stepRoutes[step], {state: getNavigationState()});
    }
  };

  const getFormError = () => isPerfectStoreSetupInvalid(formData);

  const getCustomModal = (type) => {
    switch (type) {
      case 'CANCEL_MODAL':
        return getPerfectStoreCancelModal({
          show: isCancel,
          onConfirm: onClickPrimaryButton,
          onClose: handleCancel
        });
      case 'NAME_VALIDATION_MODAL':
        return getModal({
          title: 'Duplicate Name Detected',
          text: (
            <>
              It looks like there's already a Perfect Store with this name.
              <br />
              To avoid confusion, please enter a unique name before continuing.
            </>
          ),
          showPrimaryButton: true,
          primaryButtonText: 'Edit Name',
          onClickPrimaryButton: handleValidationModalClose,
          showSecondaryButton: false,
          showModal: !isNameValid,
          onCloseModal: handleValidationModalClose,
          hideCloseButton: true
        });
      default:
        return null;
    }
  };

  return (
    <>
      <PerfectStoreWizardLayout
        currentStep={1}
        title='Create Perfect Store'
        onStepClick={handleStepClick}
        FormError={getFormError()}
        handleCancel={handleCancel}
        draftAction={handleDraftAction}
        nextAction={handleNextAction}
        disableDraft={disableDraft}>
        <PerfectStoreSetUp formData={formData} setFormData={setFormData} />
      </PerfectStoreWizardLayout>
      {isCancel && getCustomModal('CANCEL_MODAL')}
      {!isNameValid && getCustomModal('NAME_VALIDATION_MODAL')}
    </>
  );
};

export default AddPerfectStore;
